<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import IntegrationsAPI from 'dashboard/api/integrations';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: { PageHeader, NextButton },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      uazapiUrl: '',
      uazapiToken: '',
      ignoreGroups: false,
      signMessages: true,
      createNewConversation: false,
      submitting: false,
      errorMessage: '',
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
  },
  validations: {
    inboxName: { required },
    uazapiUrl: { required, url },
    uazapiToken: { required },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      this.submitting = true;
      this.errorMessage = '';
      try {
        const { data } = await IntegrationsAPI.connectUazapi({
          inbox_name: this.inboxName.trim(),
          uazapi_url: this.uazapiUrl.trim(),
          uazapi_token: this.uazapiToken.trim(),
          ignore_groups: this.ignoreGroups,
          sign_messages: this.signMessages,
          create_new_conversation: this.createNewConversation,
        });
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: data.inbox_id },
        });
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          this.$t('INBOX_MGMT.ADD.UAZAPI.ERRORS.GENERIC');
        useAlert(this.errorMessage);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.UAZAPI.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.UAZAPI.DESC')"
    />
    <form class="flex flex-col mx-0 gap-4" @submit.prevent="createChannel">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.UAZAPI.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.UAZAPI.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.UAZAPI.INBOX_NAME.ERROR') }}
        </span>
      </label>

      <label :class="{ error: v$.uazapiUrl.$error }">
        {{ $t('INBOX_MGMT.ADD.UAZAPI.URL.LABEL') }}
        <input
          v-model="uazapiUrl"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.UAZAPI.URL.PLACEHOLDER')"
          @blur="v$.uazapiUrl.$touch"
        />
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.UAZAPI.URL.HELP') }}
        </p>
        <span v-if="v$.uazapiUrl.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.UAZAPI.URL.ERROR') }}
        </span>
      </label>

      <label :class="{ error: v$.uazapiToken.$error }">
        {{ $t('INBOX_MGMT.ADD.UAZAPI.TOKEN.LABEL') }}
        <input
          v-model="uazapiToken"
          type="password"
          autocomplete="off"
          :placeholder="$t('INBOX_MGMT.ADD.UAZAPI.TOKEN.PLACEHOLDER')"
          @blur="v$.uazapiToken.$touch"
        />
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.UAZAPI.TOKEN.HELP') }}
        </p>
        <span v-if="v$.uazapiToken.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.UAZAPI.TOKEN.ERROR') }}
        </span>
      </label>

      <label class="flex items-center gap-2 mt-2">
        <input v-model="ignoreGroups" type="checkbox" />
        <span>{{ $t('INBOX_MGMT.ADD.UAZAPI.IGNORE_GROUPS') }}</span>
      </label>

      <label class="flex items-center gap-2">
        <input v-model="signMessages" type="checkbox" />
        <span>{{ $t('INBOX_MGMT.ADD.UAZAPI.SIGN_MESSAGES') }}</span>
      </label>

      <label class="flex items-center gap-2">
        <input v-model="createNewConversation" type="checkbox" />
        <span>{{ $t('INBOX_MGMT.ADD.UAZAPI.CREATE_NEW_CONVERSATION') }}</span>
      </label>

      <p v-if="errorMessage" class="text-n-ruby-9 text-sm">{{ errorMessage }}</p>

      <div class="w-full mt-4">
        <NextButton
          type="submit"
          solid
          blue
          :is-loading="submitting"
          :label="$t('INBOX_MGMT.ADD.UAZAPI.SUBMIT')"
        />
      </div>

      <div class="text-sm text-n-slate-11 bg-n-alpha-3 rounded-md p-3 mt-2">
        <strong>{{ $t('INBOX_MGMT.ADD.UAZAPI.BETA_TITLE') }}</strong>
        <p>{{ $t('INBOX_MGMT.ADD.UAZAPI.BETA_MESSAGE') }}</p>
      </div>
    </form>
  </div>
</template>
